import React, { type ReactElement } from "react"
import classNames from "classnames"
import { TableCell, TableRow, Checkbox, Tooltip, IconButton } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import { map } from "lodash"
import { useHover } from "hooks/hover"

interface ColumnProps {
  id: string
  resource: {
    resourceType: string
    isHovering?: boolean
    shortType: string
    alias: string
  }
  hasModels: boolean
  type: {
    name: string
  }
}

type ColumnNode = ReactElement<ColumnProps>

type UnknownRowProps = Record<string, unknown>

interface RowProps {
  key: string
  editable?: boolean
  deletable?: boolean
  columns: ColumnNode[]
}

interface OPTableRowProps {
  row: RowProps
  item?: UnknownRowProps | undefined
  noActions?: boolean
  isItemSelected?: boolean
  noDelete?: boolean
  noEdit?: boolean
  onRowClick?: (() => void) | undefined
  dispatchSelect: (({ type, id, checked }: { type: string, id: string, checked: boolean }) => void)
  setMenu: ({ anchor, id, item }: { anchor: EventTarget & HTMLButtonElement, id: string, item: UnknownRowProps | undefined }) => void
  columns: ColumnProps[]
}

export default function OPTableRow({
  row,
  noActions,
  isItemSelected,
  noEdit,
  noDelete,
  onRowClick,
  dispatchSelect,
  setMenu,
  columns,
  item,
  ...rowProps
}: OPTableRowProps) {
  const [isHovering, hoverBind] = useHover()
  const { editable, deletable } = row
  const bindActions = typeof hoverBind === "object" ? hoverBind : {}
  const hasRowAction = Boolean(onRowClick)

  return <TableRow
    hover
    selected={isItemSelected}
    {...bindActions}
    {...rowProps}
    className={classNames("material-table-row", { hover: isHovering && hasRowAction })}
    onClick={onRowClick}
  >
    {!noActions && !noDelete // Remove the cell if the delete action is not possible
      && <TableCell padding="checkbox">
        {deletable // Remove only the checkbox if this row cannot be deleted
          && <Checkbox
            className={`check-${row.key}`}
            checked={isItemSelected}
            onClick={event => event.stopPropagation()}
            onChange={(_, checked) => { dispatchSelect({ type: "single", id: row.key, checked }) }}
          />}
      </TableCell>}
    {map(row.columns, (column, columnIndex) => {
      const extraProps = { isHovering }
      const content = React.isValidElement(column) && columnIndex === 0
        ? React.cloneElement(column, { ...column.props, ...extraProps })
        : column
      return <TableCell key={columnIndex} className={columns[columnIndex].id}><span>{content}</span></TableCell>
    })}
    {!noActions && !noEdit // Remove the cell if the edit action is not possible
      && <TableCell width="10%" align="right">
        {editable // Remove only the menu if this row cannot be edited
          && <Tooltip title="Modify">
            <IconButton
              data-id={`${row.key}-actions`}
              onClick={(event) => {
                event.stopPropagation()
                setMenu({
                  anchor: event.currentTarget,
                  id: row.key,
                  item,
                })
              }}
              size="large"
            >
              <MoreVert />
            </IconButton>
          </Tooltip>}
      </TableCell>}
  </TableRow>
}
